import { useState, useEffect } from "react";
import {
  Skeleton,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Chip,
} from "@mui/material";
import { getDogById, getPedigree } from "../services";
import { useParams, Link } from "react-router";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import dayjs from "dayjs";

/* ── Pedigree helpers ───────────────────────────────────── */

type PedigreeNode = {
  name: string;
  id?: number;
  sex?: string;
  ws_dog?: boolean;
  external_url?: string | null;
  father?: PedigreeNode | null;
  mother?: PedigreeNode | null;
};

/* ── Pedigree table ─────────────────────────────────────── */

const pedCellSx = {
  border: "1px solid #d4c4a8",
  fontFamily: '"EB Garamond", serif',
  color: "#4a3728",
  py: 1,
  px: 1.5,
  fontSize: "0.85rem",
  verticalAlign: "middle",
};

function PedigreeTable({ tree }: { tree: PedigreeNode }) {
  /** Traverse the tree by a path string: 'f' = father, 'm' = mother */
  const getNode = (path: string): PedigreeNode | undefined => {
    let node: PedigreeNode | undefined = tree;
    for (const step of path) {
      if (!node) return undefined;
      node =
        step === "f" ? (node.father ?? undefined) : (node.mother ?? undefined);
    }
    return node;
  };

  const cellSxForSex = (sex?: string) => ({
    ...pedCellSx,
    ...(sex === "male" && {
      backgroundColor: "rgba(100,149,237,0.07)",
    }),
    ...(sex === "female" && {
      backgroundColor: "rgba(219,112,147,0.07)",
    }),
  });

  const renderCell = (node?: PedigreeNode, path?: string) => {
    if (!node?.name) {
      if (path) {
        const siblingPath =
          path.slice(0, -1) + (path.endsWith("f") ? "m" : "f");
        const sibling = getNode(siblingPath);
        if (sibling?.name) {
          return <span style={{ color: "#9e8e7e" }}>?</span>;
        }
      }
      return null;
    }
    if (node.ws_dog && node.id) {
      return (
        <Link
          to={`/dogs/${node.id}`}
          style={{ color: "#4a3728", textDecoration: "none" }}
        >
          {node.name}
        </Link>
      );
    }
    if (!node.ws_dog && node.external_url) {
      return (
        <a
          href={node.external_url}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            color: "#4a3728",
            textDecoration: "none",
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          {node.name}
          <OpenInNewIcon sx={{ fontSize: "0.85rem" }} />
        </a>
      );
    }
    return <span style={{ color: "#9e8e7e" }}>{node.name}</span>;
  };

  /*
   * 3-generation pedigree – rows are built dynamically so that
   * empty sub-trees are collapsed instead of rendering blank rows.
   */
  type CellDef = { path: string; rowSpan?: number };

  const buildRows = (): CellDef[][] => {
    const result: CellDef[][] = [];

    for (const p of ["f", "m"]) {
      const hasAnyGP = getNode(p + "f")?.name || getNode(p + "m")?.name;

      if (!hasAnyGP) {
        // No grandparents – single row for this parent
        result.push([{ path: p, rowSpan: 1 }]);
        continue;
      }

      const gpRows: CellDef[][] = [];

      for (const g of ["f", "m"]) {
        const gpPath = p + g;
        const hasAnyGGP =
          getNode(gpPath + "f")?.name || getNode(gpPath + "m")?.name;

        if (!hasAnyGGP) {
          gpRows.push([{ path: gpPath, rowSpan: 1 }]);
        } else {
          gpRows.push([{ path: gpPath, rowSpan: 2 }, { path: gpPath + "f" }]);
          gpRows.push([{ path: gpPath + "m" }]);
        }
      }

      gpRows[0].unshift({ path: p, rowSpan: gpRows.length });
      result.push(...gpRows);
    }

    return result;
  };

  const rows = buildRows();

  return (
    <TableContainer sx={{ overflowX: "auto" }}>
      <Table size="small" sx={{ borderCollapse: "collapse", minWidth: 500 }}>
        <TableBody>
          {rows.map((row, i) => (
            <TableRow key={i}>
              {row.map((cell, j) => {
                const node = getNode(cell.path);
                return (
                  <TableCell
                    key={j}
                    rowSpan={cell.rowSpan || 1}
                    sx={cellSxForSex(node?.sex)}
                  >
                    {renderCell(node, cell.path)}
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

/* ── SingleDog page ─────────────────────────────────────── */

export const SingleDog = () => {
  const { id } = useParams<{ id: string }>();
  const [details, setDetails] = useState<any>({});
  const [loading, setLoading] = useState(true);
  const [pedigree, setPedigree] = useState<PedigreeNode | null>(null);
  const [photos, setPhotos] = useState<string[]>([]);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    Promise.all([getDogById(id), getPedigree(id)])
      .then(([dogRes, pedRes]) => {
        setDetails(dogRes.data);
        setPedigree(pedRes.data);
        setPhotos(dogRes.data.gallery || []);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [id]);

  const imageUrl = details.image || "";

  const capitalize = (s: string) =>
    s ? s.charAt(0).toUpperCase() + s.slice(1) : "";

  const infoFields = [
    { label: "Sex", value: capitalize(details.sex) },
    {
      label: "Born",
      value: dayjs(details.born, "DD/MM/YYYY").format("MMMM D, YYYY"),
    },
    { label: "Color", value: details.color },
    { label: "Dysplasia", value: details.dysplasia },
    { label: "Breeder", value: details.breeder },
    { label: "Owner", value: details.owner },
  ].filter((f) => f.value);

  const titles: string[] = (details.titles || [])
    .map((t: any) =>
      typeof t === "string"
        ? t.trim()
        : typeof t.title === "string"
          ? t.title.trim()
          : "",
    )
    .filter(Boolean);

  const hasContent = details.content?.rendered?.replace(/<[^>]*>/g, "").trim();
  const hasPedigree = pedigree?.father?.name || pedigree?.mother?.name;

  if (loading) {
    return (
      <Box className="article-main">
        <Skeleton variant="text" width="40%" height={48} sx={{ mb: 1 }} />
        <Skeleton
          variant="rectangular"
          height={300}
          sx={{ borderRadius: 2, mb: 2 }}
        />
        <Skeleton variant="text" width="60%" />
        <Skeleton variant="text" width="80%" />
      </Box>
    );
  }

  return (
    <Box className="article-main">
      {/* Title */}
      <Typography
        variant="h4"
        sx={{
          fontFamily: '"Cinzel", serif',
          color: "#4a3728",
          borderBottom: "2px solid #d4b483",
          pb: 1,
          mb: 3,
        }}
      >
        {details.name}
      </Typography>

      {/* Image + Info */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          gap: 3,
          mb: 3,
        }}
      >
        {imageUrl && (
          <Box
            component="img"
            src={imageUrl}
            alt={details.name}
            sx={{
              width: { xs: "100%", md: 320 },
              maxHeight: 400,
              objectFit: "cover",
              borderRadius: 2,
              border: "1px solid #d4c4a8",
            }}
          />
        )}

        {infoFields.length > 0 && (
          <TableContainer sx={{ flex: 1 }}>
            <Table size="small">
              <TableBody>
                {infoFields.map(({ label, value }) => (
                  <TableRow key={label}>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        color: "#4a3728",
                        border: "none",
                        fontFamily: '"Cinzel", serif',
                        width: 120,
                        py: 0.75,
                        pl: 0,
                      }}
                    >
                      {label}
                    </TableCell>
                    <TableCell
                      sx={{ color: "#6b5b4a", border: "none", py: 0.75 }}
                    >
                      {value}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* Titles */}
      {titles.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="h6"
            sx={{
              fontFamily: '"Cinzel", serif',
              color: "#4a3728",
              mb: 1,
            }}
          >
            Titles
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {titles.map((t, i) => (
              <Chip
                key={i}
                label={t}
                size="small"
                sx={{
                  backgroundColor: "rgba(212,180,131,0.25)",
                  color: "#4a3728",
                  fontFamily: '"EB Garamond", serif',
                }}
              />
            ))}
          </Box>
        </Box>
      )}

      {/* Pedigree */}
      {hasPedigree && pedigree && (
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="h6"
            sx={{
              fontFamily: '"Cinzel", serif',
              color: "#4a3728",
              mb: 1,
            }}
          >
            Pedigree
          </Typography>
          <PedigreeTable tree={pedigree} />
        </Box>
      )}

      {photos.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="h6"
            sx={{
              fontFamily: '"Cinzel", serif',
              color: "#4a3728",
              mb: 1,
            }}
          >
            Photos
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "1fr 1fr",
                md: "1fr 1fr 1fr",
              },
              gap: 2,
            }}
          >
            {photos.map((url, i) => (
              <Box
                key={i}
                component="img"
                src={url}
                alt={`${details.name} photo ${i + 1}`}
                sx={{
                  width: "100%",
                  height: 220,
                  objectFit: "cover",
                  borderRadius: 2,
                  border: "1px solid #d4c4a8",
                }}
              />
            ))}
          </Box>
        </Box>
      )}

      {/* WordPress content */}
      {hasContent && (
        <Box
          sx={{
            color: "#4a3728",
            lineHeight: 1.8,
            "& img": { maxWidth: "100%", borderRadius: 1 },
          }}
          dangerouslySetInnerHTML={{
            __html: details.content?.rendered || "",
          }}
        />
      )}
    </Box>
  );
};
